<template>
  <main :class="bem()">
    <HeroCarousel
      name="landing"
      :content="pageContent.hero"
    />

    <ArticleNavigation
      v-if="navigation && navigation.length"
      :links="[{ title: 'All', slug: '/explore' }, ...navigation]"
    />
    
    <ArticleCardGrid
      v-if="content && content.length"
      :articles="content"
    />

    <ClientOnly>
      <InfiniteScroll
        :enough="!moreAvailable"
        @load-more="loadMore"
      />
    </ClientOnly>
  </main>
</template>

<script setup>
import { buildExploreListingGql } from '@/gql/explore/exploreListing.gql.js'
import { modelLightweightArticle } from '@/models/content/article.js'
import { modelLightweightAthlete } from '@/models/content/athlete.js'
import { modelExplore } from '@/models/content/explore-landing.js'

const bem = useBEM('ExploreListing')

const PRO_TEAM_RATIO = 3

// Meta setup - ideally this would be moved outside the component
definePageMeta({
  name: 'ExploreListing',
  pageType: 'Explore Landing Page'
})

const route = useRoute()
const categoryId = route.meta?.categoryId || null

const listingQueryVariables = {
  limit: 15,
  offset: 0
}

// Fetch landing page data with useDatoQuery
const {
  data,
  error,
  execute
} = await useDatoQuery({
  query: buildExploreListingGql(categoryId),
  variables: listingQueryVariables
})

const { $sentry, $head } = useNuxtApp()

// Error handling
if (error.value) {
  $sentry.captureException(error.value)
  console.log('Error fetching explore listing', error.value)
  throw createError({
    statusCode: 500,
    statusMessage: 'Internal Server Error',
    fatal: true
  })
}

// Head setup - ideally this would be moved outside the component
const { getTitle } = useMetaHelper()
useHead({
  ...$head(data.value.exploreLanding.meta),
  title: getTitle(data.value.exploreLanding.title)
})

// Computed Properties
const allArticles = computed(() => data.value?.allArticles)
const articles = computed(
  () => allArticles.value?.map(modelLightweightArticle) || []
)

const allAthletes = computed(() => data.value?.allAthletes)
const athletes = computed(
  () => allAthletes.value?.map(modelLightweightAthlete) || []
)

const category = computed(() =>
  data.value?.allArticleCategories.find(category => category.id === categoryId)
)

const pageContent = computed(() => modelExplore(data.value?.exploreLanding))
const navigation = computed(() => pageContent.value?.navigation)

const content = computed(() => {
  if (category.value?.slug === 'pro-team') return athletes.value

  // Don't mix in pro team with articles if category is set
  if (category.value?.slug) return articles.value

  const shuffledArticles = [...articles.value]
  const shuffledAthletes = [...athletes.value]
  const combinedContent = []

  while (shuffledArticles.length) {
    combinedContent.push(shuffledArticles.shift())
    if (
      combinedContent.length % PRO_TEAM_RATIO === 0
      && shuffledAthletes.length
    ) {
      combinedContent.push(shuffledAthletes.shift())
    }
  }
  return combinedContent
})

const articleCount = computed(() => data.value?.articlesMeta?.count || 0)
const athleteCount = computed(() => data.value?.athletesMeta?.count || 0)

const moreArticlesAvailable = computed(
  () => articleCount.value > articles.value.length
)
const moreAthletesAvailable = computed(
  () => athleteCount.value > athletes.value.length
)
const moreAvailable = computed(() => {
  if (!category.value) {
    return moreArticlesAvailable.value || moreAthletesAvailable.value
  } else if (category.value === 'pro-team') {
    return moreAthletesAvailable.value
  } else {
    return moreArticlesAvailable.value
  }
})

// Methods
const loadMore = async () => {
  if (moreAvailable.value) {
    listingQueryVariables.limit += 15
    await execute()
  }
}
</script>

<style lang="postcss">
</style>
